/**
 * Unified Navigation Owner for Footy Arena UI (§10).
 *
 * Single owner for keyboard, gamepad, and mouse navigation.
 * - Keyboard: Arrows / WASD move focus, Enter selects, Escape returns.
 * - Gamepad: D-pad / Left stick (0.4 deadzone, 220ms repeat delay) move focus,
 *   Button 0 (A/Cross) selects, Button 1 (B/Circle) returns.
 * - Mouse: Hover sets focus softly without fighting gamepad/keyboard.
 * - Gamepad polling loop stops cleanly on dispose().
 */

export interface NavOwnerOptions {
  getNavElements: () => HTMLElement[];
  onSelect?: (element: HTMLElement) => void;
  onBack?: () => void;
  onFocusChange?: (element: HTMLElement, index: number) => void;
}

export interface NavOwner {
  setSelectedIndex(index: number): void;
  getSelectedIndex(): number;
  dispose(): void;
}

const DEADZONE = 0.4;
const REPEAT_DELAY_MS = 220;

export function createNavOwner(opts: NavOwnerOptions): NavOwner {
  let selectedIndex = 0;
  let disposed = false;
  let lastNavTime = 0;
  let rafId = 0;

  const updateFocus = (newIndex: number): void => {
    const elements = opts.getNavElements();
    if (elements.length === 0) return;

    selectedIndex = (newIndex + elements.length) % elements.length;
    const target = elements[selectedIndex];
    if (target) {
      target.focus?.({ preventScroll: true });
      target.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      opts.onFocusChange?.(target, selectedIndex);
    }
  };

  const move = (dir: 'left' | 'right' | 'up' | 'down'): void => {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - lastNavTime < REPEAT_DELAY_MS) return;
    lastNavTime = now;

    const delta = dir === 'right' || dir === 'down' ? 1 : -1;
    updateFocus(selectedIndex + delta);
  };

  const triggerSelect = (): void => {
    const elements = opts.getNavElements();
    const target = elements[selectedIndex];
    if (target) {
      opts.onSelect?.(target);
      target.click?.();
    }
  };

  const triggerBack = (): void => {
    opts.onBack?.();
  };

  // ── Keyboard Listener ──
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;

    switch (e.key) {
      case 'ArrowLeft':
      case 'a':
      case 'A':
        e.preventDefault();
        move('left');
        break;
      case 'ArrowRight':
      case 'd':
      case 'D':
        e.preventDefault();
        move('right');
        break;
      case 'ArrowUp':
      case 'w':
      case 'W':
        e.preventDefault();
        move('up');
        break;
      case 'ArrowDown':
      case 's':
      case 'S':
        e.preventDefault();
        move('down');
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        triggerSelect();
        break;
      case 'Escape':
        e.preventDefault();
        triggerBack();
        break;
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', onKeyDown);
  }

  // ── Gamepad Polling Loop ──
  let prevBtnA = false;
  let prevBtnB = false;

  const pollGamepad = (): void => {
    if (disposed) return;

    if (typeof navigator !== 'undefined' && navigator.getGamepads) {
      const gamepads = navigator.getGamepads();
      for (const gp of gamepads) {
        if (!gp || !gp.connected) continue;

        const axisX = gp.axes[0] ?? 0;
        const axisY = gp.axes[1] ?? 0;

        const dUp = gp.buttons[12]?.pressed ?? false;
        const dDown = gp.buttons[13]?.pressed ?? false;
        const dLeft = gp.buttons[14]?.pressed ?? false;
        const dRight = gp.buttons[15]?.pressed ?? false;

        if (dLeft || axisX < -DEADZONE) move('left');
        else if (dRight || axisX > DEADZONE) move('right');
        else if (dUp || axisY < -DEADZONE) move('up');
        else if (dDown || axisY > DEADZONE) move('down');

        const btnA = gp.buttons[0]?.pressed ?? false;
        if (btnA && !prevBtnA) triggerSelect();
        prevBtnA = btnA;

        const btnB = gp.buttons[1]?.pressed ?? false;
        if (btnB && !prevBtnB) triggerBack();
        prevBtnB = btnB;
      }
    }

    if (typeof requestAnimationFrame !== 'undefined') {
      rafId = requestAnimationFrame(pollGamepad);
    }
  };

  if (typeof requestAnimationFrame !== 'undefined') {
    rafId = requestAnimationFrame(pollGamepad);
  }

  return {
    setSelectedIndex(idx: number): void {
      updateFocus(idx);
    },
    getSelectedIndex(): number {
      return selectedIndex;
    },
    dispose(): void {
      disposed = true;
      if (typeof cancelAnimationFrame !== 'undefined' && rafId) {
        cancelAnimationFrame(rafId);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('keydown', onKeyDown);
      }
    },
  };
}
